import React from 'react';

// Intraday-specific (kept):
import DailyPerformanceStrip from './DailyPerformanceStrip';

// Day-ahead structural shell:
import AnalyticsHero from '../../../../components/analytics/AnalyticsHero';
import AnalyticsStatStrip from '../../../../components/analytics/AnalyticsStatStrip';
import DistributionStripeCards from '../../../../components/analytics/DistributionStripeCards';
import PeerLeaderboardRail from '../../../../components/analytics/PeerLeaderboardRail';
import LeagueIneligibilityBanner from '../../../../components/analytics/LeagueIneligibilityBanner';
import LeagueTimelineCard from '../../../../components/analytics/LeagueTimelineCard';
import DailyScoreChart from '../../../../components/analytics/DailyScoreChart';
import GateClosurePerformanceCard from '../../../../components/analytics/GateClosurePerformanceCard';
import { useExcludedDays } from '../../../../contexts/ExcludedDaysContext';

/**
 * Intraday detail layout for the Analytics page.
 *
 * Wears the day-ahead structural shell with the same section ordering
 * day-ahead uses:
 *
 *   1. AnalyticsHero
 *   2. AnalyticsStatStrip (4 cells)
 *   3. DailyPerformanceStrip   ★ Intraday-specific (Rankings)
 *      DailyScoreChart                                  (Errors)
 *   4. DistributionStripeCards + PeerLeaderboardRail   (Rankings)
 *      LeagueTimelineCard + PeerLeaderboardRail        (Errors)
 *   5. GateClosurePerformanceCard — mode-aware horizontal-bar card per
 *      gate hour. Rankings: avg_rank; Errors: avg_score_w_pen. Mirrors
 *      day-ahead Rankings exactly.
 *
 * See spec: docs/superpowers/specs/2026-04-26-intraday-layout-parity-design.md
 */
export default function IntradayDetailLayout({
  mode = 'rankings',

  // Hero / strip.
  heroProps = null,
  horizonLabel = null,
  stripCells = [],

  // Gate Closure Performance card (mode-aware: rank for Rankings, error
  // for Errors). Replaces the previous pill-bar + drill-down section.
  gateClosureData = [],
  gateClosureLoading = false,
  monthlyStatsData = null,
  monthlyStatsLoading = false,
  selectedYear,
  selectedMonth,

  onDayClick,
  dayClickDisabledHint,

  // Rankings: distribution + peer
  distributionCards = [],

  // Shared: peer leaderboard
  peerRows = [],
  selfUserId,
  maxMissedDays = 5,
  blurSensitive = false,
  onSelectForecaster = null,

  // Errors: daily chart + league timeline
  dailyScoresForChart = [],
  leagueThresholds = null,
  userScore = null,
  monthLabel = '',
  metricLabel = '',
  currentThresholds = null,
  // Download name of the daily chart's CSV (see DailyScoreChart).
  dailyScoresFileName,
  // Authoritative league assignment from the engine — passed through to
  // LeagueTimelineCard so the badge reflects the backend's rank-based
  // tier rather than a local score-vs-threshold inference (which breaks
  // when the qualified pool is too small to anchor every threshold).
  leagueId = null,
}) {
  // Intraday outage days: hatched in the daily strip and chart.
  const excludedDays = useExcludedDays(0, selectedYear, selectedMonth);

  if (monthlyStatsLoading && !gateClosureData.length) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
          ))}
        </div>
        <div className="h-64 rounded-lg border border-gray-300 dark:border-gray-600" />
        <div className="h-80 rounded-lg border border-gray-300 dark:border-gray-600" />
      </div>
    );
  }

  const isRankings = mode === 'rankings';

  // Rankings strip: active days the user didn't qualify on come from the
  // penalty-filled score series (the rank series omits them entirely). They
  // carry a real target_day, so the strip can render them as clickable "NQ".
  const missedDays = isRankings && Array.isArray(monthlyStatsData?.daily_scores_json)
    ? monthlyStatsData.daily_scores_json
      .filter((d) => d && d.is_penalty && d.target_day)
      .map((d) => d.target_day)
    : [];

  return (
    <div className="space-y-6">
      {/* 0. Eligibility status banner — Errors-only. League rewards (and
          therefore qualification status) are an errors/score-driven concept;
          Rankings is about daily-rank performance, where the threshold has
          no bearing. The banner self-hides when the user is within the
          allowance, so the mode gate is the actual mode switch. */}
      {!isRankings ? (
        <LeagueIneligibilityBanner
          nDaysWithPenalties={monthlyStatsData?.n_days_w_penalties}
          maxMissedDays={maxMissedDays}
        />
      ) : null}

      {/* 1. Hero */}
      {heroProps && (
        <AnalyticsHero
          eyebrow={heroProps.eyebrow ?? null}
          headline={heroProps.headline}
          lede={heroProps.lede}
          horizonLabel={horizonLabel}
        />
      )}

      {/* 2. Global stats (4 cells) */}
      {stripCells.length > 0 && (
        <AnalyticsStatStrip cells={stripCells} className="mb-4" />
      )}

      {/* 3. Daily Rank Performance (Rankings) | Daily Score Chart (Errors) */}
      {monthlyStatsLoading ? (
        <div className="h-28 rounded-lg border border-gray-300 dark:border-gray-600 animate-pulse" />
      ) : isRankings ? (
        (monthlyStatsData?.daily_ranks_json?.length > 0 || missedDays.length > 0) && (
          <DailyPerformanceStrip
            data={monthlyStatsData?.daily_ranks_json || []}
            mode={mode}
            year={selectedYear}
            month={selectedMonth}
            monthLabel={monthLabel}
            onDayClick={onDayClick ? (targetDay) => onDayClick(targetDay, 0) : undefined}
            dayClickDisabledHint={dayClickDisabledHint}
            missedDays={missedDays}
            excludedDays={excludedDays}
          />
        )
      ) : (
        dailyScoresForChart.length > 0 && (
          <DailyScoreChart
            className="mb-4"
            dailyScores={dailyScoresForChart}
            thresholds={leagueThresholds}
            metricLabel={metricLabel}
            monthLabel={monthLabel}
            onDayClick={onDayClick ? (dateStr) => onDayClick(dateStr, 0) : undefined}
            dayClickDisabledHint={dayClickDisabledHint}
            excludedDays={excludedDays}
            exportFileName={dailyScoresFileName}
          />
        )
      )}

      {/* 4. Distribution + Peer (Rankings) | League + Peer (Errors) */}
      <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        {isRankings ? (
          <DistributionStripeCards
            orientation="vertical"
            eyebrow="Distribution"
            headline="Where your sessions landed"
            description="How often you finished in each rank band over this period."
            cards={distributionCards}
          />
        ) : (
          <LeagueTimelineCard
            userScore={userScore}
            thresholds={currentThresholds}
            leagueId={leagueId}
            axisFloor={peerRows[0]?.score}
            metricLabel={metricLabel}
          />
        )}
        <PeerLeaderboardRail
          rows={peerRows}
          selfUserId={selfUserId}
          mode={isRankings ? 'rankings' : 'errors'}
          metricLabel={isRankings ? 'Avg Rank' : metricLabel}
          thresholds={isRankings ? undefined : currentThresholds}
          showPaymentCutoff={isRankings}
          maxMissedDays={maxMissedDays}
          blurSensitive={blurSensitive}
          onSelectForecaster={onSelectForecaster}
        />
      </div>

      {/* 5. Gate Closure Performance — horizontal-bar card per gate hour
          (mode-aware: avg_rank for Rankings, avg_score_w_pen for Errors).
          Mirrors day-ahead Rankings GateClosurePerformanceCard. */}
      <GateClosurePerformanceCard
        className="mb-4"
        mode={mode}
        gateClosureData={gateClosureData}
        loading={gateClosureLoading}
        totalForecasters={monthlyStatsData?.nr_participants}
        metricLabel={metricLabel}
      />

    </div>
  );
}
